import { RootState } from '@/models/root-state.model';
import { createSelector } from 'reselect';
import { isValidEmail } from '@/utils/utils';
import { getLocaleByCountryCode, isEmpty } from '@/shared/utility';
import {
  selectBtagPrefix,
  selectLocale,
  selectIsROC,
  selectIsThailand,
} from '@/store/selectors/commonSelectors';
import { FluidUserData } from '@fluidpayments/types';
import { selectFluidKycStatus } from '@/store/selectors/kycSelectors';
import {
  envIsCanada,
  envIsMexico,
  envIsNormal,
  envIsSweden,
  FRENCH_ALPHA_CODES,
  COUNTRY_CODE_CANADA,
  SUBDIVISION_CODE_ALBERTA,
  matchesRegion,
} from '@/constants/constants';
import { isCuracao } from '@/utils/logic.utils';
import { BffUser } from '@lucky7ventures/bff-types';
import { selectLifetimeDeposit, selectLastSuccessfulDeposit } from '@/store/wallet/selectors';
import { BffUserTagsResponse } from '@lucky7ventures/bff-types';

export const selectUser = (state: RootState): BffUser | null => state.user.user;
export const selectUserLoading = (state: RootState): boolean => state.user.userLoading;
export const selectUserTags = (state: RootState): BffUserTagsResponse => state.user.tags;

export const selectTagsLoaded = (state: RootState): boolean => state.user.tagsLoaded;

export const selectUserId = createSelector(selectUser, user => user?.userId);

export const selectUserFirstName = createSelector(selectUser, user => user?.firstName);
export const selectUserFullName = createSelector(selectUser, user =>
  user && user.lastName && user.firstName ? user.firstName + ' ' + user.lastName : '',
);
export const selectUserAlphaCode3 = createSelector(selectUser, user => user?.countryCode3);

// in Hygraph CMS we have an enum called PlayerTag, and all the values are uppercase + underscores instead of spaces
export const selectUserTagsAsCmsEnum = createSelector(selectUserTags, tags =>
  tags.map(tag => tag.name.toUpperCase().replace(/\s+/g, '_')),
);

export const selectIsFastRegRest = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'Fastreg'),
);
export const selectUserSpecificTag = (tagName: string) =>
  createSelector(
    selectUserTags,
    tags => !!tags.find(tag => tag.name.toLocaleLowerCase() === tagName.toLocaleLowerCase()),
  );

export const selectIsFastRegTh = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'FastregTh'),
);

export const selectIsFastRegMx = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'Fastreg_mx'),
);

export const selectIsFastRegThDone = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'FastregThDone'),
);

export const selectIsFastReg = createSelector(
  selectIsFastRegRest,
  selectIsFastRegTh,
  selectIsFastRegMx,
  (fastRegRest, fastRegTh, fastRegMx) => fastRegRest || fastRegTh || fastRegMx,
);

export const selectUserLocale = createSelector(selectUser, user => {
  if (isEmpty(user) || user === null) {
    return null;
  }

  const userLocale = getLocaleByCountryCode(user.countryCode);

  // Check if French or Canadian French
  if (userLocale === 'ca' && FRENCH_ALPHA_CODES.includes(user.languageCode)) {
    return 'ca-fr';
  }

  return userLocale;
});

export const selectUserCurrency = createSelector(selectUser, (user): string => {
  if (!user) {
    return '';
  }
  return user.currency;
});

export const selectUserEmail = createSelector(selectUser, (user): string => {
  if (!user) {
    return '';
  }

  if (!isValidEmail(user.email)) {
    return '';
  }

  return user.email;
});

export const selectUserHasValidContactInfo = createSelector(selectUser, (user): boolean => {
  if (!user) {
    return false;
  }

  return !!user.mobile && isValidEmail(user.email);
});

export const selectHardReloadPending = createSelector(
  selectLocale,
  selectUserLocale,
  selectUser,
  (locale, userLocale, user) => {
    if (isEmpty(user)) {
      return false;
    }

    return locale !== userLocale;
  },
);

export const selectUserIsGameTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.name === 'Game Tester');
});

export const selectDisableLocationSmart = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.name === 'Disable LocationSmart');
});

export const selectUserHasFixedDepositLimit = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.name === 'RG Fixed Limit');
});

export const selectUserIsFluidTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.find(tag => tag.name === 'EnableFluid');
});

export const selectUserIsRewardReelTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return !!tags.find(tag => tag.name === 'SplashtechHidden');
});

export const selectIsUserWheelRestricted = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return !!tags.find(tag => tag.name === 'WheelRestricted');
});

export const selectIsUserMobileNumberVerified = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return !!tags.find(tag => tag.name === 'mobile_number_verified');
});

export const selectFluidUserData = createSelector(
  selectFluidKycStatus,
  selectLifetimeDeposit,
  selectLocale,
  selectUserFullName,
  selectUserTags,
  selectLastSuccessfulDeposit,
  selectBtagPrefix,
  (
    kycStatus,
    lifetimeDeposits,
    locale,
    fullName,
    userTags,
    lastSuccessfulDeposit,
    btagPrefix,
  ): FluidUserData => {
    let userData: FluidUserData = { fullName };

    // For Canada we don't need the lifetime deposit amount for the KYC flow, we just need the KYC status
    if (envIsCanada()) {
      userData.kycStatus = kycStatus;
      userData.lifetimeDeposit = 0;
    }

    if ((envIsMexico() || isCuracao(locale)) && kycStatus) {
      userData.kycStatus = lifetimeDeposits !== null ? kycStatus : 'unverified';
      userData.lifetimeDeposit = lifetimeDeposits || 0;
    }

    // For ROC we need to set KYC status in order for Fluid to show verify
    // prompt on withdrawal if user hasn't KYCed.
    if (envIsNormal() && ['ca', 'ca-fr'].includes(locale)) {
      userData.kycStatus = kycStatus;
    }

    userData.affiliateId = btagPrefix;
    userData.tags = userTags.map(tag => tag.name);
    userData.previousDeposit = lastSuccessfulDeposit ?? undefined;

    return userData;
  },
);

export const selectIdScanTag = createSelector(selectUserTags, tags =>
  Boolean(tags.find(tag => tag.name === 'GBG_ID_TEST')),
);
export const selectFastRegMxV2Tag = createSelector(selectUserTags, tags =>
  Boolean(tags.find(tag => tag.name === 'fastreg_mx_v2')),
);
export const selectKYCMXTag = createSelector(selectUserTags, tags =>
  Boolean(tags.find(tag => tag.name === 'kyc_mx')),
);

export const selectIsWheelDepositorTag = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'wheel_depositor'),
);

export const selectAppReviewTag = createSelector(
  selectUserTags,
  tags => !!tags.find(tag => tag.name === 'AppReview'),
);

// True when the user is subject to restricted mode. This is the case for:
//  - the entire Sweden market (sweden build), regardless of login state;
//  - the entire Thailand market (luckydays.com/th — locale 'th'), regardless of
//    login state; or
//  - a logged-in user on ROC (luckydays.com/ca — envIsNormal + ca/ca-fr locale)
//    whose registered address subdivision is Alberta (ca-ab). The Alberta branch is
//    keyed on the registered address only — geo location does not restrict.
export const selectIsRestrictedMode = createSelector(
  selectIsROC,
  selectIsThailand,
  selectUser,
  (isROC, isThailand, user): boolean => {
    if (envIsSweden() || isThailand) {
      return true;
    }

    if (!isROC) {
      return false;
    }

    return (
      !!user &&
      matchesRegion(user.countryCode, COUNTRY_CODE_CANADA) &&
      matchesRegion(user.subdivision?.code, SUBDIVISION_CODE_ALBERTA)
    );
  },
);
